import { buildGoalReviewCandidate } from '../../utils/goalReviewCandidate';
import {
  buildAllergyReviewCandidate,
  isAllergySuggestion,
} from '../../utils/allergyReviewCandidate';
import {
  buildDocumentDomainCandidatesFromFindings,
} from '../../utils/documentReviewCandidate';
import { buildCareSourceMedicationReviewCandidate } from '../../utils/medicationReviewCandidate';
import {
  REVIEWABLE_ORIGIN_COLLECTIONS,
  REVIEWABLE_ORIGIN_TYPES,
} from '../reviewableItems/reviewableItemFlow';
import { getCareSourceKnowledgeSuggestions } from './careSourceStatusModel';
import {
  CAPTURE_DESTINATIONS,
  getCaptureSuggestionDestination,
} from './captureDestinationPolicy';
import {
  buildMedicationFromSuggestion,
  getMedicationSourceSuggestions,
} from './medicationSourceSuggestions';

const normalizeText = (value = '') => String(value || '').trim();

export const REVIEWABLE_CARE_KNOWLEDGE_TYPES = Object.freeze({
  GOAL: 'goal',
  ALLERGY: 'allergy',
});

const isGoalSuggestion = (suggestion = {}) => (
  normalizeText(suggestion.knowledgeType).toLowerCase() === REVIEWABLE_CARE_KNOWLEDGE_TYPES.GOAL
);

const NON_ACTIONABLE_STATUSES = new Set([
  'added',
  'already_known',
  'archived',
  'auto_applied',
  'completed',
  'duplicate',
  'hidden',
  'ignored',
  'resolved',
  'skipped',
  'unchanged',
]);

const isActionableSuggestion = (source = {}, suggestion = {}) => {
  if (suggestion.archived === true || suggestion.hidden === true) return false;
  if (source.directProfileReviewDecisions?.[suggestion.clientId]) return false;
  const status = normalizeText(
    suggestion.reviewStatus
    || suggestion.classificationStatus
    || suggestion.deduplicationStatus
  ).toLowerCase();
  return !NON_ACTIONABLE_STATUSES.has(status);
};

export const splitReviewableCareKnowledgeSuggestions = (source = {}) => {
  const suggestions = getCareSourceKnowledgeSuggestions(source);
  return suggestions.reduce((groups, suggestion) => {
    if (isGoalSuggestion(suggestion) || isAllergySuggestion(suggestion)) {
      groups.reviewable.push(suggestion);
    } else if (
      getCaptureSuggestionDestination(suggestion) !== CAPTURE_DESTINATIONS.CARE_KNOWLEDGE
    ) {
      groups.structured.push(suggestion);
    } else {
      groups.generic.push(suggestion);
    }
  return groups;
  }, {
    reviewable: [],
    structured: [],
    generic: [],
  });
};

const getPersistedDocumentFindings = (source = {}) => (
  source.documentFindings
  || source.metadata?.careSourceProcessing?.documentFindings
  || null
);

export const getStructuredCandidateDestination = (domain = '') => {
  if (domain === 'allergy') return 'Allergies';
  if (domain === 'medication') return 'Medications';
  if (domain === 'timeline_fact') return 'Timeline';
  if (domain === 'document') return 'Document';
  return 'Goals';
};

export const buildReviewableCandidatesFromCareSource = (source = {}, { includeMedication = false } = {}) => {
  const sourceId = normalizeText(source.id);
  if (!sourceId) return [];

  const { reviewable } = splitReviewableCareKnowledgeSuggestions(source);
  const documentCandidates = buildDocumentDomainCandidatesFromFindings({
    originId: sourceId,
    childId: source.childId || '',
    findings: getPersistedDocumentFindings(source) || {},
  });
  const medicationCandidates = includeMedication ? getMedicationSourceSuggestions(source)
    .filter((suggestion) => suggestion.needsReview !== false)
    .map((suggestion) => buildCareSourceMedicationReviewCandidate({
      source,
      candidate: {
        id: suggestion.id,
        suggestion,
        suggestedAction: suggestion.planAction || 'add_new',
        nextMedication: buildMedicationFromSuggestion(suggestion, source),
      },
    }))
    .filter((candidate) => candidate?.key && candidate?.label) : [];

  const candidates = reviewable
    .filter((suggestion) => isActionableSuggestion(source, suggestion))
    .map((suggestion) => {
      const buildCandidate = isAllergySuggestion(suggestion)
        ? buildAllergyReviewCandidate
        : buildGoalReviewCandidate;
      return buildCandidate({
      origin: {
        id: sourceId,
        type: REVIEWABLE_ORIGIN_TYPES.CARE_SOURCE,
        collection: REVIEWABLE_ORIGIN_COLLECTIONS.CARE_SOURCES,
        childId: source.childId || '',
        sourceText: suggestion.sourceText || source.rawText || source.cleanedText || '',
        metadata: {
          suggestionClientId: suggestion.clientId,
          sourceContext: source.sourceContext || source.sourceMetadata?.sourceContext || '',
        },
      },
      extractedItem: {
        title: suggestion.title,
        content: suggestion.content,
        summary: suggestion.summary,
        description: suggestion.content,
        category: suggestion.knowledgeType,
        source: REVIEWABLE_ORIGIN_COLLECTIONS.CARE_SOURCES,
        sourceText: suggestion.sourceText || suggestion.content,
      },
      confidence: suggestion.confidence,
      });
    })
    .filter((candidate) => candidate?.key);

  return [...new Map([
    ...documentCandidates,
    ...medicationCandidates,
    ...candidates,
  ].map((candidate) => [candidate.key, candidate])).values()];
};

export const buildGenericCareKnowledgeSource = (source = {}) => {
  const { generic } = splitReviewableCareKnowledgeSuggestions(source);
  return {
    ...source,
    suggestedCareKnowledgeItems: generic,
    careKnowledgeSuggestionSummary: {
      ...(source.careKnowledgeSuggestionSummary || {}),
      itemCount: generic.length,
      needsReview: generic.length > 0,
    },
  };
};
